import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard } from '@capacitor/keyboard';
import { SocialLogin } from '@capgo/capacitor-social-login';

let socialLoginInitPromise: Promise<void> | null = null;

export async function ensureSocialLoginInitialized() {
  if (!getIsNativePlatform()) return;

  if (!socialLoginInitPromise) {
    socialLoginInitPromise = (async () => {
      const initOptions: Record<string, unknown> = {
        google: {
          iOSClientId: process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID || '',
          webClientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
        },
        facebook: {
          appId: process.env.REACT_APP_FACEBOOK_APP_ID || '',
        },
      };

      // Avoid passing an empty Apple config on Android: plugin rejects initialize()
      // and prevents Google/Facebook providers from being registered.
      if (getIsIOS()) {
        initOptions.apple = {};
      }

      await SocialLogin.initialize(initOptions);
    })();
  }

  return socialLoginInitPromise;
}

/**
 * Runtime-safe checks that re-evaluate Capacitor state each call.
 * Use these in critical paths (e.g. OAuth state generation) where module-level
 * constants may have been captured before the native bridge was ready.
 */
export function getIsNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}
export function getIsIOS(): boolean {
  return Capacitor.getPlatform() === 'ios';
}
export function getIsAndroid(): boolean {
  return Capacitor.getPlatform() === 'android';
}
export function getIsNativeAndroid(): boolean {
  return getIsNativePlatform() && getIsAndroid();
}
export function getIsNativeiOS(): boolean {
  return getIsNativePlatform() && getIsIOS();
}

/**
 * Initialize native Capacitor plugins.
 * Safe to call on web — every call is guarded by getIsNativePlatform().
 */
export async function initCapacitor() {
  if (!getIsNativePlatform()) return;

  // Hide splash screen once the app is mounted
  await SplashScreen.hide();

  // Status bar: light content on the brand color background
  if (getIsIOS()) {
    await StatusBar.setStyle({ style: Style.Light });
  }
  if (getIsAndroid()) {
    await StatusBar.setStyle({ style: Style.Light });
    await StatusBar.setOverlaysWebView({ overlay: false });
  }

  // Initialize native social sign-in providers before first login attempt.
  await ensureSocialLoginInitialized();

  // Handle hardware back button / app URL open for deep links
  App.addListener('appUrlOpen', (event) => {
    const url = new URL(event.url);

    // Skip custom URL schemes that we don't route in React Router
    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
      return;
    }

    const path = url.pathname + url.search;
    if (path) {
      // Use window.location so React Router picks it up
      window.location.href = path;
    }
  });

  // Keyboard: adjust viewport when keyboard appears
  if (getIsIOS()) {
    Keyboard.addListener('keyboardWillShow', () => {
      document.body.classList.add('keyboard-visible');
    });
    Keyboard.addListener('keyboardWillHide', () => {
      document.body.classList.remove('keyboard-visible');
    });
  }
  if (getIsAndroid()) {
    Keyboard.addListener('keyboardDidShow', () => {
      document.body.classList.add('keyboard-visible');
    });
    Keyboard.addListener('keyboardDidHide', () => {
      document.body.classList.remove('keyboard-visible');
    });
  }
}
